import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Alert,
  Divider,
} from '@mui/material';
import {
  Search as AnalyzeIcon,
  ExpandMore as ExpandIcon,
  Gavel as DecisionIcon,
} from '@mui/icons-material';
import ModeSelector from '../../components/Navigation/ModeSelector';
import EvidenceCard from '../../components/Decision/EvidenceCard';
import ConfidenceIndicator from '../../components/Decision/ConfidenceIndicator';
import KnowledgeGapPanel from '../../components/Decision/KnowledgeGapPanel';
import { decisionApi } from '../../services/api/decision.service';
import { DecisionAnalysis } from '../../types/decision.types';

/**
 * DecisionPage
 *
 * Decision Mode interface where users input a thesis and see
 * categorized evidence (supporting vs. contradicting) with
 * confidence scoring and knowledge gap identification.
 *
 * Reads ?thesis= URL param to pre-fill and auto-analyze
 * (for graph-to-decision navigation).
 */
const DecisionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [thesis, setThesis] = useState('');
  const [analysis, setAnalysis] = useState<DecisionAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neutralExpanded, setNeutralExpanded] = useState(false);
  const [gapsExpanded, setGapsExpanded] = useState(false);

  /**
   * Perform thesis analysis
   */
  const analyzeThesis = useCallback(async (thesisText: string) => {
    if (!thesisText.trim()) return;

    setLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      const result = await decisionApi.analyzeThesis(thesisText.trim());
      setAnalysis(result);
    } catch (err: any) {
      console.error('Error analyzing thesis:', err);
      const errMsg = err.response?.data?.error;
      setError(typeof errMsg === 'string' ? errMsg : err.message || 'Failed to analyze thesis. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Auto-analyze if thesis is provided in URL params
   */
  useEffect(() => {
    const urlThesis = searchParams.get('thesis');
    if (urlThesis) {
      setThesis(urlThesis);
      analyzeThesis(urlThesis);
    }
  }, [searchParams, analyzeThesis]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    analyzeThesis(thesis);
  };

  const handleViewInGraph = (nodeUid: string) => {
    navigate(`/knowledge-graph?highlight=${nodeUid}`);
  };

  const handleSearchGap = (suggestedSearch: string) => {
    setThesis(suggestedSearch);
    analyzeThesis(suggestedSearch);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        style={{
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 12,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <ModeSelector size="small" showLabels />
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={1} alignItems="center">
            <DecisionIcon style={{ color: '#4DC4D2' }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Decision Mode
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      <Container maxWidth="lg" style={{ flex: 1, paddingTop: 24, paddingBottom: 24, overflow: 'auto' }}>
        {/* Thesis Input */}
        <Paper
          elevation={0}
          style={{
            padding: 24,
            marginBottom: 24,
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: 8,
          }}
        >
          <Typography variant="h6" gutterBottom>
            What do you want to evaluate?
          </Typography>
          <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
            Enter a thesis, claim, or question. We'll search your knowledge graph for supporting and contradicting evidence.
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="e.g., Remote work improves productivity..."
                value={thesis}
                onChange={(e) => setThesis(e.target.value)}
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<AnalyzeIcon />}
                disabled={!thesis.trim() || loading}
                style={{
                  minWidth: 140,
                  backgroundColor: '#4DC4D2',
                }}
              >
                {loading ? 'Analyzing...' : 'Analyze'}
              </Button>
            </Stack>
          </form>
        </Paper>

        {/* Error */}
        {error && (
          <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div>
            <Skeleton variant="rectangular" height={12} style={{ marginBottom: 16, borderRadius: 4 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rectangular" height={120} style={{ borderRadius: 4 }} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rectangular" height={120} style={{ borderRadius: 4 }} />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </div>
        )}

        {/* Results */}
        {analysis && !loading && (
          <div>
            {/* Confidence Indicator */}
            <Paper elevation={0} style={{ padding: 16, marginBottom: 24, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}>
              <ConfidenceIndicator
                supportingCount={analysis.supporting.length}
                contradictingCount={analysis.contradicting.length}
                neutralCount={analysis.neutral.length}
                overallConfidence={analysis.overallConfidence}
              />
              <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
                Analyzed in {analysis.processingTimeMs}ms
              </Typography>
            </Paper>

            {/* Overall Analysis Summary */}
            {analysis.summary && (
              <Paper
                elevation={0}
                style={{
                  padding: 24,
                  marginBottom: 24,
                  border: '1px solid #4DC4D2',
                  borderRadius: 8,
                  backgroundColor: 'rgba(77, 196, 210, 0.04)',
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, color: '#4DC4D2' }}>
                  Analysis Summary
                </Typography>
                <Typography variant="body2" style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                  {analysis.summary}
                </Typography>
              </Paper>
            )}

            {/* Two-column evidence */}
            <Grid container spacing={3} style={{ marginBottom: 24 }}>
              {/* Supporting Evidence */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 600, marginBottom: 12, color: '#4DC4D2' }}
                >
                  Supporting Evidence ({analysis.supporting.length})
                </Typography>
                {analysis.supporting.length === 0 ? (
                  <Paper elevation={0} style={{ padding: 16, textAlign: 'center', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No supporting evidence found.
                    </Typography>
                  </Paper>
                ) : (
                  analysis.supporting.map((evidence, idx) => (
                    <EvidenceCard
                      key={`support-${idx}`}
                      evidence={evidence}
                      onViewInGraph={handleViewInGraph}
                    />
                  ))
                )}
              </Grid>

              {/* Contradicting Evidence */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 600, marginBottom: 12, color: '#E94257' }}
                >
                  Contradicting Evidence ({analysis.contradicting.length})
                </Typography>
                {analysis.contradicting.length === 0 ? (
                  <Paper elevation={0} style={{ padding: 16, textAlign: 'center', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No contradicting evidence found.
                    </Typography>
                  </Paper>
                ) : (
                  analysis.contradicting.map((evidence, idx) => (
                    <EvidenceCard
                      key={`contradict-${idx}`}
                      evidence={evidence}
                      onViewInGraph={handleViewInGraph}
                    />
                  ))
                )}
              </Grid>
            </Grid>

            {/* Neutral Evidence (Collapsed) */}
            {analysis.neutral.length > 0 && (
              <Accordion
                expanded={neutralExpanded}
                onChange={() => setNeutralExpanded(!neutralExpanded)}
                elevation={0}
                style={{ marginBottom: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}
              >
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Neutral Evidence ({analysis.neutral.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {analysis.neutral.map((evidence, idx) => (
                      <Grid item xs={12} md={6} key={`neutral-${idx}`}>
                        <EvidenceCard
                          evidence={evidence}
                          onViewInGraph={handleViewInGraph}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Knowledge Gaps */}
            {analysis.knowledgeGaps.length > 0 && (
              <Accordion
                expanded={gapsExpanded}
                onChange={() => setGapsExpanded(!gapsExpanded)}
                elevation={0}
                style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}
              >
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Knowledge Gaps ({analysis.knowledgeGaps.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: 0 }}>
                  <KnowledgeGapPanel
                    gaps={analysis.knowledgeGaps}
                    onSearchGap={handleSearchGap}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </div>
        )}

        {/* Empty state (no analysis yet) */}
        {!analysis && !loading && !error && (
          <div style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
            <DecisionIcon style={{ fontSize: 64, color: 'rgba(0,0,0,0.38)', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Evaluate claims against your knowledge
            </Typography>
            <Typography variant="body2" color="text.secondary" style={{ maxWidth: 480, margin: '0 auto' }}>
              Enter a thesis above and click Analyze. Decision Mode will search your knowledge graph
              for supporting and contradicting evidence, helping you make informed decisions.
            </Typography>
          </div>
        )}
      </Container>
    </div>
  );
};

export default DecisionPage;
